import type { ImapKonto, MailAnbieter, MailAnbieterListe } from "./typen";

/** W82 Web-10b W10B-1 (S98-1, P-2): die Anbieterliste gilt nur in der Form des Vertrags: ein Objekt mit nicht leerem Feld
 *  `anbieter`, jeder Eintrag mit Text `schluessel` und `name`, `host` Text oder null, `domains` eine Liste von Texten. Alles
 *  andere (alter Server mit 405 oder 404, jeder andere Fehler, `[]`, `{}`, eine fremde Form) heißt: Blatt wie bisher.
 *  W82 Web-11a W11A-3 (ZP Web-10b B-8, S98-1 F01): dazu genau ein Eintrag `sonstiger`, als letzter und mit `host` null; jeder
 *  andere mit einem Host als nicht leerem Text. */
export function anbieterListeGueltig(daten: unknown): MailAnbieterListe | null {
  if (typeof daten !== "object" || daten === null || Array.isArray(daten)) return null;
  const anbieter = (daten as { anbieter?: unknown }).anbieter;
  if (!Array.isArray(anbieter) || anbieter.length === 0) return null;
  const gut = anbieter.every((a: unknown) => {
    if (typeof a !== "object" || a === null || Array.isArray(a)) return false;
    const e = a as Record<string, unknown>;
    return (
      typeof e.schluessel === "string" &&
      e.schluessel !== "" &&
      typeof e.name === "string" &&
      e.name !== "" &&
      (typeof e.host === "string" || e.host === null) &&
      Array.isArray(e.domains) &&
      e.domains.every((d) => typeof d === "string")
    );
  });
  if (!gut) return null;
  const eintraege = anbieter as { schluessel: string; host: string | null }[];
  const sonstige = eintraege.filter((e) => e.schluessel === "sonstiger");
  const vertrag = sonstige.length === 1 && eintraege.at(-1) === sonstige[0] && sonstige[0]?.host === null && eintraege.every((e) => e.schluessel === "sonstiger" || (typeof e.host === "string" && e.host.trim() !== ""));
  return vertrag ? (daten as MailAnbieterListe) : null;
}

/** Rand-Leerraum einer Domain: jedes Zeichen mit der Unicode-Eigenschaft `White_Space` (auch U+0085, das `trim` lässt) und
 *  U+FEFF wie bei `trim` (ZP Web-10b B-1) */
const RAND = /^[\p{White_Space}\uFEFF]+|[\p{White_Space}\uFEFF]+$/gu;

/** Domain einer Mailadresse: Text nach dem letzten „@“, ohne Leerraum am Rand, klein; ohne „@“ keine. */
export function domainAus(benutzer: string): string | null {
  const at = benutzer.lastIndexOf("@");
  if (at < 0) return null;
  const domain = benutzer.slice(at + 1).replace(RAND, "").toLowerCase();
  return domain || null;
}

/** F-W10-1 = A: trifft die Domain genau eine Domain der Liste, ist das der Vorschlag; nur exakt, keine Unterdomäne. Die Adresse
 *  geht dabei nirgends hin, gerechnet wird nur hier. */
export function anbieterVorschlag(benutzer: string, anbieter: readonly MailAnbieter[]): string | null {
  const domain = domainAus(benutzer);
  if (!domain) return null;
  const treffer = anbieter.filter((a) => a.domains.some((d) => d.trim().toLowerCase() === domain));
  return treffer.length === 1 ? (treffer[0]?.schluessel ?? null) : null;
}

/** Link der Anleitung nur mit `https://` (D-1697: bis zum Nachtrag sind alle Links null). */
export function sichererLink(link: unknown): string | null {
  return typeof link === "string" && /^https:\/\/\S+$/.test(link) ? link : null;
}

// W82 Web-11a W11A-3 (S98-3: Integer; ZP Web-10b B-3): nur ganze Zahlen ab 0, keine Bruchzahl, nichts jenseits der sicheren
// Ganzzahlen (`1e21`)
const istZahl = (w: unknown): w is number => typeof w === "number" && Number.isSafeInteger(w) && w >= 0;
const istZeit = (w: unknown): boolean => typeof w === "string" && w !== "";

const S98_3 = ["erstabruf_gelesen", "erstabruf_gesamt", "erstabruf_fertig"] as const;

/** W10B-2 (3), K-7: Fortschritt des Erstabrufs aus einem der beiden Feldsätze. `gesamt` ist null, solange der Server die Zahl
 *  noch nicht kennt. W82 Web-11a W11A-3 (ZP Web-10b B-3, R1 Web-10b H-3, K3d-W11A-6): steht im Konto einer der Schlüssel von
 *  S98-3 (`erstabruf_gelesen`, `erstabruf_gesamt`, `erstabruf_fertig`), gilt nur dieser Satz, mit allen drei Schlüsseln
 *  (`erstabruf_fertig` Zeitpunkt oder null); passt er nicht, gibt es keinen Fortschritt und keinen Rückfall. Ohne jeden Schlüssel
 *  von S98-3 gilt S96-5 (`gelesen`, `fenster_gesamt`, `fertig_am`) wie bisher. `gelesen` über `gesamt` passt nie (S98-3 (4):
 *  `gesamt = gelesen + gefunden`). */
export function fortschritt(k: ImapKonto): { gelesen: number; gesamt: number | null; fertig: boolean } | null {
  const satz = (gelesen: unknown, gesamt: unknown, fertig: unknown) =>
    istZahl(gelesen) && (gesamt === null || (istZahl(gesamt) && gelesen <= gesamt)) && (fertig === null || fertig === undefined || istZeit(fertig))
      ? { gelesen, gesamt: gesamt as number | null, fertig: istZeit(fertig) }
      : null;
  const felder = k as unknown as Record<string, unknown>;
  if (S98_3.some((s) => felder[s] !== undefined)) {
    if (S98_3.some((s) => felder[s] === undefined)) return null;
    return satz(k.erstabruf_gelesen, k.erstabruf_gesamt, k.erstabruf_fertig);
  }
  return satz(k.gelesen, k.fenster_gesamt, k.fertig_am);
}

/** „{n} von {m} Mails gelesen“, solange der Erstabruf läuft: nicht fertig, `gesamt` eine Zahl, Konto an, kein Fehler. Sonst
 *  null, und die Zeile bleibt wie bisher. Zahlen ohne Tausenderpunkt wie die Planseite. */
export function fortschrittText(k: ImapKonto): string | null {
  const f = fortschritt(k);
  if (!f || f.fertig || f.gesamt === null || !k.aktiv || k.fehler) return null;
  return `${f.gelesen} von ${f.gesamt} ${f.gesamt === 1 ? "Mail" : "Mails"} gelesen`;
}

/** Neuladen der Postfächer (W10B-2 (4), L-5): läuft bei einem Konto der Erstabruf und ist die erste Zahl da (`gelesen` über 0
 *  oder `gesamt` eine Zahl), alle 30 s; ein Konto, das noch nie gelesen wurde, wie bisher alle 3 s (das ist auch der Takt bis zur
 *  ersten Zahl). Ein Bestandskonto mit den Standardwerten (0, null) lädt so nicht im 3-s-Takt. */
export function imapTakt(konten: readonly ImapKonto[] | undefined): number | false {
  let takt: number | false = false;
  for (const k of konten ?? []) {
    if (!k.aktiv) continue;
    const f = fortschritt(k);
    if (f && !f.fertig && !k.fehler && (f.gelesen > 0 || f.gesamt !== null)) takt = 30_000;
    else if (!k.zuletzt) return 3000;
  }
  return takt;
}
